import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormRadioGroup, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
  calculateTargetAmount,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import {
  type OtherGoalFormType as CreateOtherGoalFormType,
  DrawType,
  getOtherGoalFormSchema,
  minDate,
} from "Forms/Schemas/Goals/otherGoalFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getGetAnalysisGoalsQueryKey,
  getGetOtherGoalQueryKey,
  useCreateOtherGoal,
  useUpdateOtherGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  OtherGoalDataPayoutFrequency,
  type OtherGoalDto,
  OtherGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { OtherGoalModelingHint } from "./OtherGoalModelingHint";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly onDrawTypeChange: (drawType: DrawType) => void;
  readonly editGoal?: OtherGoalDto;
  readonly goalId?: string;
};

export const OtherGoalOneTimeForm = forwardRef<{}, Props>(({ onDrawTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const defaultValues = useMemo(() => {
    if (editGoal) {
      return {
        includeFee: editGoal.modeling.fee?.enabled,
        includeInflation: editGoal.modeling.inflation?.enabled,
        feeRate: Number(editGoal.modeling.fee?.rate) || 0,
        inflationRate: Number(editGoal.modeling.inflation?.rate) || 0,
        name: editGoal.name,
        drawStart: new Date(editGoal.data.targetDate as string),
        targetAmount: Number(editGoal.data.payoutAmount?.value) || 0,
        interestRate: Number(editGoal.modeling.appreciationRate) || 0,
        note: editGoal.note || "",
        priority: editGoal.priorityLevel === OtherGoalDtoPriorityLevel.high,
        drawType: DrawType.LumpSum,
        referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
      };
    }

    return {
      includeFee: true,
      includeInflation: true,
      feeRate: DEFAULT_INFLATION_RATE,
      inflationRate: DEFAULT_INFLATION_RATE,
      name: t(Resources.AnalysisDetail.Goals.GoalType.Other),
      monthlyContribution: 0,
      monthlyContributionWithFee: 0,
      interestRate: INTEREST_RATE_CONSERVATIVE,
      drawType: DrawType.LumpSum,
      referenceStartDate: new Date(),
    };
  }, [editGoal, t]);

  const methods = useForm<CreateOtherGoalFormType>({
    resolver: yupResolver(getOtherGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues,
  });

  const { control, setValue, watch, handleSubmit, clearErrors } = methods;

  const drawType = watch("drawType");
  const drawStart = watch("drawStart");
  const targetAmount = watch("targetAmount");
  const feeRate = watch("feeRate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const referenceStartDate = watch("referenceStartDate");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingMonthlyContributionWithFee, setPendingMonthlyContributionWithFee] = useState<number | null>(null);
  const [lastAutoMonthlyContributionWithFee, setLastAutoMonthlyContributionWithFee] = useState<number | null>(null);

  const investmentLength = differenceInMonths(drawStart, new Date());
  const isInvalidDrawStartDate = isBefore(startOfDay(drawStart), startOfDay(minDate));
  const shouldRenderChart = drawStart && !isInvalidDrawStartDate && monthlyContribution > 0 && targetAmount > 0;

  const { mutate: createOtherGoal } = useCreateOtherGoal();

  const { mutate: updateOtherGoal } = useUpdateOtherGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
  }));

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
    if (interestRate !== undefined && targetAmount !== undefined && durationInMonths !== undefined) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);

      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  }, [feeRate, inflationRate, interestRate, targetAmount, includeInflation, setValue, drawStart, isInvalidDrawStartDate]);

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    clearErrors("drawStart");
    onChangeDrawStartDate();
  }, [drawStart, isInvalidDrawStartDate, clearErrors]);

  const onChangeDrawStartDate = () => {
    onChange({
      durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate !== undefined && targetAmount !== undefined && durationInMonths !== undefined) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount: Number(targetAmount) || 0,
        durationInMonths: Number(durationInMonths) || 0,
        interestRate: Number(interestRate) || 0,
        inflationRate: Number(inflationRate),
      });

      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);

      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  };
  const recalculateMonthlyContributionFromTarget = () => {
    const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const wf = calculateMonthlyContributionWithFee(Number(monthlyContribution), Number(includeFee ? feeRate : 0));
    const roundedWf = Math.round(Number(wf));
    setValue("monthlyContribution", monthlyContribution.toString() as any);
    setValue("monthlyContributionWithFee", roundedWf as any);
    setLastAutoMonthlyContributionWithFee(roundedWf);
  };

  const handleConfirmResult = (shouldRecalculateTarget: boolean) => {
    if (shouldRecalculateTarget && pendingMonthlyContributionWithFee != null) {
      const durationInMonths = drawStart ? differenceInMonths(drawStart, new Date()) : 0;
      const monthsToYears = durationInMonths / 12;
      const contributionWithoutFee = calculateMonthlyContributionWithoutFee(
        Number(pendingMonthlyContributionWithFee),
        Number(includeFee ? feeRate : 0),
      );

      const newTarget = calculateTargetAmount(
        Number(contributionWithoutFee),
        monthsToYears,
        Number(interestRate) || 0,
        includeInflation ? Number(inflationRate) || 0 : 0,
      );

      setValue("targetAmount", Math.round(newTarget) as any);
      setValue("monthlyContribution", contributionWithoutFee.toString() as any);
      const roundedWithFee = Math.round(Number(pendingMonthlyContributionWithFee));
      setValue("monthlyContributionWithFee", roundedWithFee as any);
      setLastAutoMonthlyContributionWithFee(roundedWithFee);
    } else {
      recalculateMonthlyContributionFromTarget();
    }

    setIsConfirmOpen(false);
    setPendingMonthlyContributionWithFee(null);
  };

  const onCreate = (data: CreateOtherGoalFormType) => {
    createOtherGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.one_time,
            payoutInterval: {
              value: 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            targetAmount: {
              value: 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            regularInvestmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEdit = (data: CreateOtherGoalFormType) => {
    updateOtherGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.one_time,
            payoutInterval: {
              value: 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            targetAmount: {
              value: 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            regularInvestmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetOtherGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <ConfirmDialog
            open={isConfirmOpen}
            title={t(Resources.AnalysisDetail.Assets.ModelingModal.Title)}
            message={t(Resources.AnalysisDetail.Goals.ConfirmRecalculateTargetFromMonthly)}
            confirmLabel={t(Resources.Common.Yes)}
            cancelLabel={t(Resources.Common.Cancel)}
            onConfirm={() => handleConfirmResult(true)}
            onCancel={() => handleConfirmResult(false)}
          />
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Other)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <FormRadioGroup
                  control={control}
                  name="drawType"
                  label={t(Resources.AnalysisDetail.Goals.DrawType)}
                  options={toRadioOptions([
                    {
                      code: DrawType.LumpSum,
                      name: t(Resources.AnalysisDetail.Goals.LumpSum),
                    },
                    {
                      code: DrawType.Monthly,
                      name: t(Resources.AnalysisDetail.Goals.Monthly),
                    },
                  ])}
                  defaultValue={DrawType.LumpSum}
                  columns={2}
                  disabled={!!editGoal || readOnly}
                  onChange={(drawType) => onDrawTypeChange(drawType === DrawType.LumpSum.toString() ? DrawType.LumpSum : DrawType.Monthly)}
                />
              </Grid>
              {drawType === DrawType.LumpSum && (
                <>
                  <Grid size={6}>
                    <FormDatePicker
                      control={control}
                      name="drawStart"
                      label={t(Resources.AnalysisDetail.Goals.DrawDate)}
                      required
                      minDate={startOfMonth(addMonths(new Date(), 1))}
                      disabled={readOnly}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="targetAmount"
                      label={t(Resources.AnalysisDetail.Goals.DrawAmount)}
                      required
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      onChange={(targetAmount) =>
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount: Number(targetAmount),
                          inflationRate,
                          interestRate,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
          {targetAmount > 0 && drawStart && !isInvalidDrawStartDate && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={12}>
                    <OtherGoalModelingHint
                      startDate={drawStart}
                      interestRate={interestRate}
                      targetAmount={targetAmount}
                      monthlyContribution={monthlyContributionWithFee}
                      referenceStartDate={referenceStartDate}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      required
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      onChange={(newContributionWithFee) => {
                        setValue("monthlyContribution", calculateMonthlyContributionWithoutFee(Number(newContributionWithFee), feeRate));
                      }}
                      onBlur={() => {
                        const currentValue = Math.round(Number(monthlyContributionWithFee));
                        if (lastAutoMonthlyContributionWithFee !== null && currentValue === Number(lastAutoMonthlyContributionWithFee)) {
                          return;
                        }
                        setPendingMonthlyContributionWithFee(currentValue);
                        setIsConfirmOpen(true);
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      required
                      scale={1}
                      min={includeInflation ? 0 : 0.1}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (
                          (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                          (!includeInflation && Number(newInterestRate) <= 0)
                        ) {
                          setValue("interestRate", interestRate);
                          return;
                        }
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: Number(newInterestRate),
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", (newInflationRate + 0.1).toString() as any);
                        } else {
                          setValue("inflationRate", newInflationRate.toString() as any);
                        }

                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate: newInflationRate,
                          interestRate,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        setValue("feeRate", newFeeRate);
                        onChange({
                          durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={!includeFee || readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                    <Typography component="span" variant="caption" fontWeight={500}>
                      {t(Resources.Common.Investment)}
                    </Typography>
                    <Typography component="span" variant="caption">
                      {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Stack>
                  <SavingsChart
                    referenceStartDate={referenceStartDate}
                    targetDate={drawStart}
                    targetAmount={targetAmount}
                    monthlyContributionAmount={Number(monthlyContribution)}
                    interestRate={interestRate}
                    inflationRate={inflationRate}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
